import { FEATURE_FLAGS } from '@constants';
import { defineStore } from 'pinia';

import { useLaunchDarkly } from '~/composables/useLaunchDarkly';
import { isThanksgivingDay } from '~/support/utils/thanksgiving';

import { Season, useSeasonalPreferencesStore } from './seasonal-preferences';

export const THANKSGIVING_EGGS = [
  {
    description: 'The sidebar logo becomes the turkey in a pilgrim hat, with the OP badge on its chest.',
    key: 'turkeyLogo',
    label: 'Turkey logo',
  },
  {
    description: 'Pies, drumsticks, corn and the rest of the feast stand in for the sidebar and page icons.',
    key: 'icons',
    label: 'Harvest icons',
  },
  {
    description: 'Every so often the turkey logo breaks into a dance. Click it any time for a gobble.',
    key: 'dancingTurkey',
    label: 'Dancing turkey',
  },
  {
    description: 'When you resolve or approve a claim, the turkey hops for joy and tosses a few leaves.',
    key: 'claimCheers',
    label: 'Claim cheers',
  },
  {
    description: 'On Thanksgiving Day the turkey wears a "Pardoned" sash; click it for its official pardon.',
    key: 'pardon',
    label: 'Turkey pardon',
  },
  {
    description: 'While something loads, a pumpkin pie gets eaten slice by slice instead of the spinner.',
    key: 'pieLoader',
    label: 'Pie loader',
  },
  { description: 'A few autumn leaves drift down the window.', key: 'leaves', label: 'Falling leaves' },
  {
    description:
      'A harvest wreath with a "Give thanks" ribbon in the bottom corner; a leaf drifts off it now and then.',
    key: 'wreath',
    label: 'Harvest wreath',
  },
  {
    description: 'Every border and divider takes on a warm harvest-amber tint.',
    key: 'harvestBorders',
    label: 'Harvest borders',
  },
  {
    description: 'Page titles and headings switch to a cosy script face.',
    key: 'harvestFont',
    label: 'Harvest headings',
  },
  { description: 'A soft candlelit glow around the edges of the window.', key: 'warmGlow', label: 'Warm glow' },
  { description: 'Type "gobble" anywhere outside a text field for a feast.', key: 'gobble', label: 'Gobble' },
] as const;

export type ThanksgivingEgg = (typeof THANKSGIVING_EGGS)[number]['key'];

export const THANKSGIVING_ROOT_CLASS = 'theme-thanksgiving';

// Prefixed so the <html> markers never collide with the decor element classes (.tg-turkey, .tg-leaf, ...).
const EGG_ROOT_CLASS: Record<ThanksgivingEgg, string> = {
  claimCheers: 'tg-egg-claim-cheers',
  dancingTurkey: 'tg-egg-dancing-turkey',
  gobble: 'tg-egg-gobble',
  harvestBorders: 'tg-egg-harvest-borders',
  harvestFont: 'tg-egg-harvest-font',
  icons: 'tg-egg-icons',
  leaves: 'tg-egg-leaves',
  pardon: 'tg-egg-pardon',
  pieLoader: 'tg-egg-pie-loader',
  turkeyLogo: 'tg-egg-turkey-logo',
  warmGlow: 'tg-egg-warm-glow',
  wreath: 'tg-egg-wreath',
};

/**
 * Thanksgiving theme state. It shows in light or dark mode while the seasonal themes flag is on, engineering has
 * switched Thanksgiving on for everyone, and the person has not switched it off for themselves.
 */
export const useThanksgivingThemeStore = defineStore('thanksgivingTheme', () => {
  const seasonal = useSeasonalPreferencesStore();
  const platformEnabled = ref(false);
  let synced = false;

  const showOnDevice = computed(() => seasonal.personalEnabled(Season.THANKSGIVING));
  const globallyEnabled = computed(() => seasonal.globalEnabled(Season.THANKSGIVING));
  const active = computed(() => platformEnabled.value && globallyEnabled.value && showOnDevice.value);

  function isEggEnabled(key: ThanksgivingEgg): boolean {
    return active.value && seasonal.eggEnabled(Season.THANKSGIVING, key);
  }

  const htmlClasses = computed(() => {
    if (!active.value) return [];

    return [
      THANKSGIVING_ROOT_CLASS,
      ...THANKSGIVING_EGGS.filter(egg => isEggEnabled(egg.key)).map(egg => EGG_ROOT_CLASS[egg.key]),
    ];
  });

  // The pardon shows on Thanksgiving Day itself, or for the rest of the session after a preview from the Themes page.
  const pardonPreview = ref(false);
  const pardoned = computed(() => isEggEnabled('pardon') && (pardonPreview.value || isThanksgivingDay(new Date())));

  function previewPardon() {
    pardonPreview.value = true;
  }

  function setShowOnDevice(value: boolean) {
    seasonal.setPersonal(Season.THANKSGIVING, value);
  }

  function setEgg(key: ThanksgivingEgg, enabled: boolean) {
    seasonal.setEgg(Season.THANKSGIVING, key, enabled);
  }

  // Reads the flag once and follows later changes; safe to call from several components.
  function sync() {
    if (synced) return;
    synced = true;

    const { getFlag, onFlagChange } = useLaunchDarkly();

    platformEnabled.value = !!getFlag(FEATURE_FLAGS.SEASONAL_THEMES, false);
    onFlagChange(FEATURE_FLAGS.SEASONAL_THEMES, value => {
      platformEnabled.value = !!value;
    });
    seasonal.load();
  }

  return {
    active,
    globallyEnabled,
    htmlClasses,
    isEggEnabled,
    pardoned,
    platformEnabled,
    previewPardon,
    setEgg,
    setShowOnDevice,
    showOnDevice,
    sync,
  };
});
